import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart3, Users, CalendarClock, ListChecks, HeartPulse, Gauge, Phone, Mail,
  Plus, ArrowUpRight, Trash2, MessageSquarePlus,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { Card, Row } from "./ContextTabs";
import { fmtDateFr, triLabel, triCls, HEALTH } from "./placementsShared";
import { useLang, translateFr as tfr } from "@/i18n";

const INT_STATUS = {
  todo: { label: tfr("À faire"), cls: "bg-secondary text-muted-foreground border-border" },
  in_progress: { label: tfr("En cours"), cls: "bg-info/10 text-info border-info/30" },
  done: { label: tfr("Terminé"), cls: "bg-success/10 text-success border-success/30" },
  blocked: { label: tfr("Bloqué"), cls: "bg-athlynx/10 text-athlynx border-athlynx/30" },
};
const WB_AVAIL = { available: "Disponible", limited: tfr("Limité"), unavailable: "Indisponible" };
const WB_TREND = { up: "En progression", stable: "Stable", down: "En baisse" };
const KIND_FR = { followup: "Suivi", event: "Agenda", reminder: tfr("Relance"), contract: tfr("Contrat"), renewal: "Renouvellement", career: tfr("Carrière") };

export function ClubContactsCard({ detail }) {
  const { tf } = useLang();
  const contacts = detail.club_contacts || [];
  return (
    <Card title={tf("Contacts au club")} Icon={Users} testid="card-club-contacts">
      {contacts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucun contact club associé.")}</p>
      ) : (
        <ul className="space-y-2.5">
          {contacts.slice(0, 4).map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
              <div className="min-w-0">
                <div className="font-medium truncate">{c.full_name || c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.role || c.title || "—"}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                {c.phone && <a href={`tel:${c.phone}`} aria-label={tf("Appeler")} className="p-1.5 rounded-md text-info hover:bg-info/10"><Phone className="h-3.5 w-3.5" /></a>}
                {c.email && <a href={`mailto:${c.email}`} aria-label={tf("Envoyer un email")} className="p-1.5 rounded-md text-info hover:bg-info/10"><Mail className="h-3.5 w-3.5" /></a>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {detail.relation?.club_id && (
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
          <Link to={`/clubs/${detail.relation.club_id}`}>{tf("Voir le club")} <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </Button>
      )}
    </Card>
  );
}

export function NextStepsList({ detail, limit, testid }) {
  const { tf } = useLang();
  const steps = (detail.next_steps || []).slice(0, limit || 50);
  if (steps.length === 0) return <p className="text-sm text-muted-foreground">{tf("Aucune échéance à venir.")}</p>;
  return (
    <ul className="space-y-2" data-testid={testid}>
      {steps.map((s, i) => (
        <li key={i}>
          <Link to={s.link || "#"} className="flex items-center justify-between gap-3 text-sm group">
            <span className="min-w-0 flex items-center gap-2">
              <span className={cn("text-[9px] uppercase tracking-wide rounded px-1.5 py-0.5 border shrink-0",
                s.overdue ? "text-athlynx border-athlynx/30 bg-athlynx/5" : "text-muted-foreground border-border")}>
                {tf(KIND_FR[s.kind] || s.kind)}
              </span>
              <span className="truncate group-hover:text-athlynx transition-colors">{s.label}</span>
            </span>
            <span className={cn("text-xs shrink-0 tabular-nums", s.overdue ? "text-athlynx" : "text-muted-foreground")}>
              {fmtDateFr(s.date)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function WellbeingCard({ detail, onRefresh }) {
  const { tf } = useLang();
  const wb = detail.wellbeing;
  const [editing, setEditing] = useState(false);
  const [av, setAv] = useState(wb?.availability || "");
  const [tr, setTr] = useState(wb?.playing_time_trend || "");
  const [note, setNote] = useState(wb?.note || "");
  const save = async () => {
    try {
      await api.put(`/placements/${detail.id}/wellbeing`, { availability: av || "", playing_time_trend: tr || "", note });
      toast.success(tf("Enregistré")); setEditing(false); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  return (
    <Card title={tf("Performance & bien-être")} Icon={HeartPulse} testid="card-wellbeing"
      action={detail.status === "ACTIVE" && (
        <button onClick={() => setEditing((v) => !v)} className="text-[11px] text-info hover:underline" data-testid="wellbeing-edit">
          {editing ? tf("Fermer") : tf("Modifier")}
        </button>)}>
      {editing ? (
        <div className="space-y-2">
          <Select value={av || "__none__"} onValueChange={(v) => setAv(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="wb-avail"><SelectValue placeholder={tf("Disponibilité")} /></SelectTrigger>
            <SelectContent><SelectItem value="__none__">—</SelectItem>
              {Object.entries(WB_AVAIL).map(([k, l]) => <SelectItem key={k} value={k}>{tf(l)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={tr || "__none__"} onValueChange={(v) => setTr(v === "__none__" ? "" : v)}>
            <SelectTrigger className="h-9 text-xs" data-testid="wb-trend"><SelectValue placeholder={tf("Temps de jeu")} /></SelectTrigger>
            <SelectContent><SelectItem value="__none__">—</SelectItem>
              {Object.entries(WB_TREND).map(([k, l]) => <SelectItem key={k} value={k}>{tf(l)}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Note (déclaratif, non médical)")} className="text-xs min-h-[56px]" data-testid="wb-note" />
          <Button size="sm" onClick={save} className="h-8" data-testid="wb-save">{tf("Enregistrer")}</Button>
        </div>
      ) : wb && (wb.availability || wb.playing_time_trend || wb.note) ? (
        <>
          <Row label={tf("Disponibilité")} value={wb.availability ? tf(WB_AVAIL[wb.availability]) : null} />
          <Row label={tf("Temps de jeu")} value={wb.playing_time_trend ? tf(WB_TREND[wb.playing_time_trend]) : null} />
          {wb.note && <p className="text-xs text-muted-foreground mt-1.5">{wb.note}</p>}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{tf("Aucune donnée enregistrée.")}</p>
      )}
    </Card>
  );
}

export function IntegrationCard({ detail, full, onRefresh }) {
  const { tf } = useLang();
  const items = detail.integration_items || [];
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const [cat, setCat] = useState("logement");
  const [due, setDue] = useState("");
  const isActive = detail.status === "ACTIVE";
  const cats = ["logement", "langue", "scolarite", "entourage", "administratif", "installation", "other"];
  const catFr = { logement: "Logement", langue: "Langue", scolarite: tfr("Scolarité / formation"), entourage: "Entourage", administratif: "Administratif", installation: "Installation", other: "Autre" };

  const add = async () => {
    if (!label.trim()) { toast.error(tf("Libellé requis")); return; }
    try {
      await api.post(`/placements/${detail.id}/integration`, { label, category: cat, due_date: due || null });
      setLabel(""); setDue(""); setAdding(false); onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };
  const setStatus = async (iid, status) => {
    try { await api.patch(`/placements/${detail.id}/integration/${iid}`, { status }); onRefresh?.(); }
    catch { toast.error(tf("Erreur")); }
  };
  const remove = async (iid) => {
    try { await api.delete(`/placements/${detail.id}/integration/${iid}`); onRefresh?.(); }
    catch { toast.error(tf("Erreur")); }
  };

  return (
    <Card title={tf("Intégration")} Icon={ListChecks} testid={full ? "integration-tab-card" : "card-integration"}
      action={isActive && (
        <button onClick={() => setAdding((v) => !v)} className="text-[11px] text-info hover:underline inline-flex items-center gap-0.5" data-testid="integration-add-toggle">
          <Plus className="h-3 w-3" /> {tf("Ajouter")}
        </button>)}>
      {adding && (
        <div className="mb-3 space-y-2 rounded-xl border border-border dark:border-border p-2.5">
          <div className="flex flex-col min-[420px]:flex-row gap-2">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger className="h-9 min-[420px]:w-[150px] text-xs" data-testid="int-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{tf(catFr[c])}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={tf("Ex. : Trouver un logement")} className="h-9 text-xs" data-testid="int-label" />
          </div>
          <div className="flex gap-2">
            <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} className="h-9 text-xs" data-testid="int-due" />
            <Button size="sm" onClick={add} className="h-9" data-testid="int-save">{tf("Ajouter")}</Button>
          </div>
        </div>
      )}
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{tf("Aucun élément d'intégration suivi.")}</p>
      ) : (
        <ul className="space-y-2">
          {(full ? items : items.slice(0, 5)).map((it) => {
            const st = INT_STATUS[it.status] || INT_STATUS.todo;
            return (
              <li key={it.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`int-item-${it.id}`}>
                <span className="min-w-0 truncate">
                  <span className="text-xs text-muted-foreground mr-1.5">{tf(catFr[it.category] || "Autre")} ·</span>
                  {it.label}
                  {it.due_date && <span className="text-xs text-muted-foreground ml-1.5">({fmtDateFr(it.due_date)})</span>}
                </span>
                <span className="flex items-center gap-1 shrink-0">
                  {isActive ? (
                    <Select value={it.status} onValueChange={(v) => setStatus(it.id, v)}>
                      <SelectTrigger className={cn("h-6 px-2 text-[10px] rounded-full border w-auto gap-1", st.cls)} data-testid={`int-status-${it.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(INT_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{tf(v.label)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={cn("text-[10px] rounded-full border px-2 py-0.5", st.cls)}>{tf(st.label)}</span>
                  )}
                  {isActive && full && (
                    <button onClick={() => remove(it.id)} aria-label={tf("Supprimer")} className="p-1 text-muted-foreground/50 hover:text-athlynx" data-testid={`int-del-${it.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export function OverviewTab({ detail, onRefresh }) {
  const { tf } = useLang();
  const a = detail.athlete || {};
  const sit = detail.current_situation;
  const h = HEALTH[detail.health?.state] || HEALTH.NORMAL;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4" data-testid="overview-grid">
      <Card title={tf("Situation au club")} Icon={BarChart3} testid="card-club-situation">
        <Row label={tf("Club")} value={detail.club || a.club} />
        {a.competition && <Row label={tf("Compétition")} value={a.competition} />}
        {a.position && <Row label={tf("Poste / discipline")} value={a.position} />}
        {(detail.stats || []).map((s) => <Row key={s.key} label={tf(s.label)} value={String(s.value)} />)}
        {(detail.stats || []).length === 0 && (
          <p className="text-xs text-muted-foreground mt-1.5">{tf("Aucune donnée de performance disponible.")}</p>
        )}
      </Card>
      <ClubContactsCard detail={detail} />
      <Card title={tf("Prochaine période clé")} Icon={CalendarClock} testid="card-key-period">
        <NextStepsList detail={detail} limit={5} testid="overview-next-steps" />
      </Card>
      <IntegrationCard detail={detail} onRefresh={onRefresh} />
      <WellbeingCard detail={detail} onRefresh={onRefresh} />
      <Card title={tf("Indicateurs de suivi")} Icon={Gauge} testid="card-indicators">
        <Row label={tf("Niveau d'attention")} value={tf(h.label)} tone={detail.health?.state !== "NORMAL" ? "text-warning" : "text-success"} />
        {[["Situation sportive", sit?.sporting], ["Relation club", sit?.club_relationship], [tfr("Satisfaction athlète"), sit?.athlete_satisfaction]].map(([l, v]) => (
          <div key={l} className="flex items-center justify-between gap-2 py-1.5 text-sm">
            <span className="text-xs text-muted-foreground">{tf(l)}</span>
            {v ? <span className={cn("text-[10px] font-medium rounded-full border px-2 py-0.5", triCls(v))}>{tf(triLabel(v))}</span>
              : <span className="text-xs text-muted-foreground/50">{tf("Non renseigné")}</span>}
          </div>
        ))}
        {detail.objective && <p className="text-xs text-muted-foreground mt-2 italic">« {detail.objective} »</p>}
      </Card>
    </div>
  );
}

export function PerformanceTab({ detail }) {
  const { tf } = useLang();
  const a = detail.athlete || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card title={tf("Situation au club")} Icon={BarChart3} testid="perf-stats-card">
        {(detail.stats || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{tf("Aucune donnée de performance disponible.")}</p>
        ) : (
          (detail.stats || []).map((s) => <Row key={s.key} label={tf(s.label)} value={String(s.value)} />)
        )}
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
          <Link to="/performance" data-testid="open-performance-link">{tf("Ouvrir Performance")} <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </Card>
      <Card title={tf("Objectifs de carrière actifs")} Icon={Gauge} testid="perf-goals-card">
        {(detail.career_goals || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">{tf("Aucun objectif de carrière actif.")}</p>
        ) : (
          <ul className="space-y-2">{detail.career_goals.map((g) => (
            <li key={g.id} className="text-sm flex justify-between gap-2"><span>{g.title}</span>
              {g.due_date && <span className="text-xs text-muted-foreground shrink-0">{fmtDateFr(g.due_date)}</span>}</li>))}
          </ul>
        )}
        <Button asChild size="sm" variant="outline" className="mt-3 h-8 gap-1.5 border-border dark:border-border">
          <Link to={`/players/${a.id}/career-plan`}>{tf("Ouvrir le plan de carrière")} <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </Button>
      </Card>
    </div>
  );
}

export function AddInteractionDialog({ open, onOpenChange, playerId, onSaved }) {
  const { tf } = useLang();
  const [type, setType] = useState("note");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const types = { call: "Appel", email: "Email", meeting: tfr("Réunion"), note: "Note", whatsapp: "WhatsApp", video: "Visio" };
  const save = async () => {
    if (!note.trim()) { toast.error(tf("Note requise")); return; }
    setSaving(true);
    try {
      await api.post("/interactions", { player_id: playerId, type, note });
      toast.success(tf("Interaction ajoutée")); setNote(""); onOpenChange(false); onSaved?.();
    } catch { toast.error(tf("Erreur")); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]" data-testid="add-interaction-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageSquarePlus className="h-4 w-4" /> {tf("Ajouter une interaction")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="interaction-type"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(types).map(([k, l]) => <SelectItem key={k} value={k}>{tf(l)}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={tf("Compte rendu, point d'étape…")} data-testid="interaction-note" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{tf("Annuler")}</Button>
          <Button onClick={save} disabled={saving} data-testid="interaction-save">{tf("Enregistrer")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
